import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isFunction, isUndefined } from 'lodash';
import { makePayment, makeBindingPayment, makeFreePayment, setSaveCard } from '../../../redux/modules/payment';
import PaymentMethodPicker from '../../PaymentMethodPicker';
import AutomaticRecurringPaymentScheduleTable from '../../AutomaticRecurringPaymentScheduleTable';
import FormSection from '../../FormComponents/FormSection';
import ScrollToTopPage from '../../ScrollToTopPage';
import InfoText from '../../MUI/IntoText';
import useSchedule from '../../../hooks/useSchedule';
import { AMD_SYMBOL, APPLICATION_CONTRACT_TYPES, PAYMENT_METHOD } from '../../../constants';
import AutomaticRecurringInfoBlock from '../../AutomaticRecurringInfoBlock';
import Navigation from './Navigation';
import { setHasAgreedAutomaticRecurring } from '../../../redux/modules/oncoCareContract';
import { useClearAllNotifications } from '../../../hooks/useClearAllNotifications';
import { updateCache } from '../../../api/contractApi';

import PaymentInfoIcon from '../../../img/formSectionIcons/paymentInfo.svg';
import useStyles from './makeStyles';

const PaymentStep = ({ goPrevious, params, step }) => {
  const {
    totalPrice,
    loader,
    paymentParams,
    disablePaymentFrequencyOption,
    isAutomaticRecurringPayment,
    paymentFrequencies,
    handleOpenMobileSummary,
    isDeviceLgSize,
    setStepNavigationBox,
    showLoader,
    isFetchingPrice,
    paymentFrequency,
    automaticRecurringFormProps,
    paymentInfoBlockBackground,
    type: contractType,
  } = params;

  const { setPaymentMethod, paymentMethod, orderId, draftToken } = paymentParams;
  const classes = useStyles();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gettingPaymentUrl = useSelector((store) => store.paymentReducer.gettingPaymentUrl);
  const { saveCard } = useSelector((store) => store.paymentReducer);
  useClearAllNotifications();

  const payBtnAmount = useMemo(
    () => (totalPrice ? ` ${AMD_SYMBOL} ${totalPrice.toLocaleString('en-US')}` : ''),
    [totalPrice],
  );
  const isFreePayment = useMemo(() => totalPrice === 0 && !isFetchingPrice && !showLoader, [
    totalPrice,
    isFetchingPrice,
    showLoader,
  ]);

  const transactionBinding = useSelector((store) => store.paymentReducer.transactionBinding);
  const freePaymentSuccessRedirectUrl = useSelector(
    (store) => store.paymentReducer.freePaymentSuccessRedirectUrl,
  );
  const [isNextValid, setIsNextValid] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);

  useEffect(() => {
    if (paymentMethod) {
      setIsNextValid(true);
    }

    const isDisabled = isFunction(disablePaymentFrequencyOption);
    if (isDisabled) {
      disablePaymentFrequencyOption(step);
    }

    return () => {
      if (isDisabled) {
        disablePaymentFrequencyOption(null);
      }
    };
  }, [disablePaymentFrequencyOption, paymentMethod, step]);

  useEffect(() => {
    const successUrl = transactionBinding.successRedirectUrl || freePaymentSuccessRedirectUrl;
    if (successUrl) {
      window.onbeforeunload = null;
      window.location.href = successUrl;
    }
  }, [transactionBinding, freePaymentSuccessRedirectUrl]);

  useEffect(() => {
    if (paymentMethod !== PAYMENT_METHOD.CARD) {
      dispatch(setSaveCard(false));
    }
  }, [dispatch, paymentMethod, setPaymentMethod]);

  useEffect(() => {
    if (isFreePayment) {
      dispatch(setPaymentMethod(PAYMENT_METHOD.FREE));
    }
  }, [dispatch, isFreePayment, setPaymentMethod]);

  useEffect(() => {
    dispatch(setSaveCard(!!isAutomaticRecurringPayment));
    if (isAutomaticRecurringPayment) {
      dispatch(setPaymentMethod(PAYMENT_METHOD.CARD));
    }
  }, [dispatch, isAutomaticRecurringPayment, setPaymentMethod]);

  useEffect(() => {
    setIsNextDisabled(!paymentMethod);
  }, [paymentMethod, isAutomaticRecurringPayment]);

  useEffect(() => {
    if (isAutomaticRecurringPayment && !isEmpty(automaticRecurringFormProps)) {
      setIsNextDisabled(!automaticRecurringFormProps.getValues().hasAgreed);
    }
  }, [isAutomaticRecurringPayment, automaticRecurringFormProps]);

  const contractSchedule = useSchedule(
    !isUndefined(paymentFrequency) ? paymentFrequency.value : null, paymentFrequencies,
  );

  const handleNext = useCallback(
    async (shouldSaveCard = false) => {
      if (!paymentMethod) {
        setIsNextValid(false);
      } else if (paymentMethod === PAYMENT_METHOD.FREE && orderId) {
        window.onbeforeunload = null;
        dispatch(makeFreePayment(orderId, contractType));
      } else if (paymentMethod.savedCard && orderId) {
        window.onbeforeunload = null;
        dispatch(makeBindingPayment(orderId, paymentMethod.savedCard));
      } else if (!paymentMethod.savedCard && orderId && draftToken) {
        window.onbeforeunload = null;
        if (contractType !== APPLICATION_CONTRACT_TYPES.ROAD_CARE) {
          await updateCache(orderId, {
            isSave: shouldSaveCard,
            paymentType: paymentMethod,
          });
        }
        dispatch(
          makePayment(
            orderId,
            paymentMethod,
            draftToken,
            contractType,
          ),
        );
      }
    },
    [contractType, dispatch, draftToken, orderId, paymentMethod],
  );

  return (
    <div>
      <ScrollToTopPage withDelay />
      <Navigation
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        handleOpenMobileSummary={handleOpenMobileSummary}
        totalPrice={totalPrice}
        goPrevious={goPrevious}
        saveCard={saveCard}
        isNextValid={isNextValid}
        handleNext={handleNext}
        isNextDisabled={isNextDisabled}
        payBtnAmount={payBtnAmount}
      />
      {(gettingPaymentUrl || transactionBinding.isLoading) && loader}
      {
        !isAutomaticRecurringPayment && (
        <FormSection title="recurringPayment.steps.paymentType" icon={PaymentInfoIcon}>
          <PaymentMethodPicker
            selectedMethod={paymentMethod}
            selectMethod={(method) => dispatch(setPaymentMethod(method))}
            savedCards={transactionBinding.savedCards}
            isValid={isNextValid}
            isFreePayment={isFreePayment}
            isAutomaticRecurringPayment={isAutomaticRecurringPayment}
          />
        </FormSection>
        )
      }
      {isAutomaticRecurringPayment && contractSchedule && (
      <>
        <FormSection title="recurringPayment.steps.paymentsSchedule" icon={PaymentInfoIcon}>
          <AutomaticRecurringPaymentScheduleTable
            paymentSchedule={contractSchedule}
          />
        </FormSection>
        <InfoText
          textInfo={t('automaticRecurringPayment.notification', { frequency: t(`paymentFrequencyOptions.paymentFrequencies.${paymentFrequency.value}`) })}
          withHTML
          background={paymentInfoBlockBackground}
        />
        <div className={classes.infoContainer}>
          <AutomaticRecurringInfoBlock
            isDisabled={false}
            checked={automaticRecurringFormProps.getValues().hasAgreed}
            errors={automaticRecurringFormProps.errors}
            handleCheckboxChange={(event) => {
              automaticRecurringFormProps.setValue('hasAgreed', event.target.checked, true);
              dispatch(setHasAgreedAutomaticRecurring(event.target.checked));
            }}
          />
        </div>
      </>
      )}
    </div>
  );
};

PaymentStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  params: PropTypes.shape({
    totalPrice: PropTypes.number,
    loader: PropTypes.element.isRequired,
    paymentParams: PropTypes.shape({
      setPaymentMethod: PropTypes.func.isRequired,
      paymentMethod: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      draftToken: PropTypes.string,
    }),
    disablePaymentFrequencyOption: PropTypes.func,
    isAutomaticRecurringPayment: PropTypes.bool,
    isAppaContract: PropTypes.bool,
    contractSchedule: PropTypes.shape({}),
    data: PropTypes.shape({
      signer: PropTypes.shape({}),
    }).isRequired,
    isDeviceLgSize: PropTypes.bool.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    setStepNavigationBox: PropTypes.func.isRequired,
    showLoader: PropTypes.bool,
    isFetchingPrice: PropTypes.bool,
    paymentFrequencies: PropTypes.shape(),
    automaticRecurringFormProps: PropTypes.shape(),
    paymentFrequency: PropTypes.shape({
      value: PropTypes.string,
    }),
    paymentInfoBlockBackground: PropTypes.string,
    type: PropTypes.string,
  }),
  step: PropTypes.shape({}).isRequired,

};

PaymentStep.defaultProps = {
  params: {
    totalPrice: null,
    paymentParams: {
      paymentMethod: null,
      orderId: null,
      draftToken: null,
    },
    disablePaymentFrequencyOption: null,
    isAutomaticRecurringPayment: false,
    isAppaContract: null,
    showLoader: false,
    isFetchingPrice: false,
    paymentFrequencies: [],
    paymentFrequency: { value: '' },
    automaticRecurringFormProps: {},
    paymentInfoBlockBackground: null,
  },
};

export default PaymentStep;
